import { useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { Settings, Save, ArrowLeft } from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { Switch } from '../components/Switch';
import { useStore } from '../hooks/useStore';
import {
  defaultPlayoffSettings,
  getTournament,
  getTournamentBySlug,
  normalizeSeasonSettings,
  updateTournament,
  updateTournamentSettings,
} from '../services/tournamentService';
import { paths, tournamentSlug } from '../lib/paths';
import type { PlayoffFormat, RegistrationIntakeType, ScoringSettings, Weekday } from '../models';

const TIEBREAKER_OPTIONS: ScoringSettings['tiebreakers'][number][] = [
  'wins',
  'goal_diff',
  'goals_for',
  'head_to_head',
  'shootout_wins',
  'fewest_pims',
];

const TIEBREAKER_LABEL: Record<ScoringSettings['tiebreakers'][number], string> = {
  wins: 'Wins',
  goal_diff: 'Goal differential',
  goals_for: 'Goals for',
  head_to_head: 'Head-to-head',
  shootout_wins: 'Shootout wins',
  fewest_pims: 'Fewest penalty minutes',
};

const WEEKDAYS: Array<{ value: Weekday; label: string }> = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export function TournamentSettingsPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const t = useStore(() => getTournament(id) ?? getTournamentBySlug(id));
  const db = useStore((state) => state);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  if (!t) {
    return (
      <div className="empty">
        <div style={{ fontWeight: 600, color: 'var(--text)' }}>Competition not found</div>
        <Link to={paths.adminTournaments}>Back to events</Link>
      </div>
    );
  }

  const s = t.settings;
  const isSeason = t.format === 'season';
  const canonicalPath = paths.adminCompetitionSettings(t.format, tournamentSlug(t));
  if (location.pathname !== canonicalPath) {
    return <Navigate to={`${canonicalPath}${location.search}${location.hash}`} replace />;
  }
  const season = normalizeSeasonSettings(s.season);
  const seasonGameIds = new Set(db.games.filter((g) => g.tournamentId === t.id).map((g) => g.id));
  const hasSchedule = seasonGameIds.size > 0;
  const hasScoring =
    db.scoreEvents.some((event) => seasonGameIds.has(event.gameId)) ||
    db.gameResults.some((result) => seasonGameIds.has(result.gameId));
  const seasonPeriodCount = s.scoring.periodCount ?? 3;
  const seasonPeriodLength = s.scoring.periodLengthMinutes ?? 12;
  const persist = (patch: Parameters<typeof updateTournamentSettings>[1]) => {
    updateTournamentSettings(t.id, patch);
    setSavedAt(new Date().toLocaleTimeString());
  };

  const moveTiebreaker = (key: ScoringSettings['tiebreakers'][number], dir: -1 | 1) => {
    const list = [...s.scoring.tiebreakers];
    const i = list.indexOf(key);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    persist({ scoring: { ...s.scoring, tiebreakers: list } });
  };

  const toggleTiebreaker = (key: ScoringSettings['tiebreakers'][number], on: boolean) => {
    const list = on
      ? [...s.scoring.tiebreakers, key]
      : s.scoring.tiebreakers.filter((k) => k !== key);
    persist({ scoring: { ...s.scoring, tiebreakers: list } });
  };

  return (
    <>
      <ModuleHero
        eyebrow="Settings"
        title={`${t.name} - settings`}
        description={`Adjust how this ${isSeason ? 'season' : 'tournament'} scores and schedules. Changes save instantly.`}
        icon={Settings}
        accent="ice"
        status={savedAt ? { label: `Saved ${savedAt}`, tone: 'ok' } : undefined}
      >
        <Link to={paths.adminCompetition(t.format, tournamentSlug(t))} className="btn btn--ghost btn--sm">
          <ArrowLeft size={14} /> Back to setup
        </Link>
      </ModuleHero>

      <div className="grid grid--2">
        <div className="card">
          <div className="card__head"><h3 className="card__title">Scoring</h3></div>
          {isSeason ? (
            <div className="stack">
              <div className="grid grid--2">
                <div className="field">
                  <label className="field__label">Periods</label>
                  <input
                    type="number"
                    min={1}
                    max={6}
                    className="input"
                    value={seasonPeriodCount}
                    onChange={(e) => {
                      const periodCount = clampInt(e.target.value, 1, 6);
                      persist({
                        scoring: {
                          ...s.scoring,
                          periodCount,
                          periodLengthMinutes: seasonPeriodLength,
                          gameLengthMinutes: periodCount * seasonPeriodLength,
                        },
                      });
                    }}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Minutes per period</label>
                  <input
                    type="number"
                    min={1}
                    max={30}
                    className="input"
                    value={seasonPeriodLength}
                    onChange={(e) => {
                      const periodLengthMinutes = clampInt(e.target.value, 1, 30);
                      persist({
                        scoring: {
                          ...s.scoring,
                          periodCount: seasonPeriodCount,
                          periodLengthMinutes,
                          gameLengthMinutes: seasonPeriodCount * periodLengthMinutes,
                        },
                      });
                    }}
                  />
                </div>
              </div>
              <div className="note">Game length: {seasonPeriodCount * seasonPeriodLength} regulation minutes.</div>
              <div className="grid grid--2">
                <div className="field">
                  <label className="field__label">Tie game rule</label>
                  <select
                    className="select"
                    value={s.scoring.overtimeFormat ?? 'straight_shootout'}
                    onChange={(e) =>
                      persist({ scoring: { ...s.scoring, overtimeFormat: e.target.value as typeof s.scoring.overtimeFormat } })
                    }
                  >
                    <option value="straight_shootout">Straight to shootout</option>
                    <option value="overtime_then_shootout">Overtime, then shootout</option>
                    <option value="continuous_overtime">Continuous sudden-death overtime</option>
                    <option value="regulation_tie">Allow ties after regulation</option>
                  </select>
                </div>
                <div className="field">
                  <label className="field__label">Shootout round size</label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="input"
                    value={s.scoring.shootoutRoundCount ?? 3}
                    onChange={(e) =>
                      persist({ scoring: { ...s.scoring, shootoutRoundCount: clampInt(e.target.value, 1, 10) } })
                    }
                  />
                </div>
              </div>
              <div className="grid grid--3">
                <div className="field">
                  <label className="field__label">OT minutes</label>
                  <input
                    type="number"
                    min={1}
                    max={20}
                    className="input"
                    value={s.scoring.overtimeMinutes ?? 5}
                    onChange={(e) =>
                      persist({ scoring: { ...s.scoring, overtimeMinutes: clampInt(e.target.value, 1, 20) } })
                    }
                  />
                </div>
                <div className="field">
                  <label className="field__label">OT skaters</label>
                  <input
                    type="number"
                    min={1}
                    max={5}
                    className="input"
                    value={s.scoring.overtimeSkaters ?? 3}
                    onChange={(e) =>
                      persist({ scoring: { ...s.scoring, overtimeSkaters: clampInt(e.target.value, 1, 5) } })
                    }
                  />
                </div>
                <div className="field">
                  <label className="field__label">OT/SO loss points</label>
                  <input
                    type="number"
                    min={0}
                    max={3}
                    className="input"
                    value={s.scoring.pointsOvertimeLoss ?? 1}
                    onChange={(e) => {
                      const value = clampInt(e.target.value, 0, 3);
                      persist({ scoring: { ...s.scoring, pointsOvertimeLoss: value, pointsShootoutLoss: value } });
                    }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="stack">
              <div className="field">
                <label className="field__label">Game length (minutes)</label>
                <input
                  type="number"
                  min={1}
                  max={120}
                  className="input"
                  value={s.scoring.gameLengthMinutes}
                  onChange={(e) =>
                    persist({ scoring: { ...s.scoring, gameLengthMinutes: Number(e.target.value || 1) } })
                  }
                />
              </div>
              <Switch
                checked={s.scoring.trackAssists}
                onChange={(v) => persist({ scoring: { ...s.scoring, trackAssists: v } })}
                label="Track assists"
                hint="Capture primary and secondary assists per goal."
              />
              <Switch
                checked={s.scoring.trackPenaltyShotsOnly}
                onChange={(v) => persist({ scoring: { ...s.scoring, trackPenaltyShotsOnly: v } })}
                label="Penalty shots only"
                hint="Penalty-shot resolution without PIM tracking."
              />
              <Switch
                checked={s.scoring.trackPenaltyMinutes}
                onChange={(v) => persist({ scoring: { ...s.scoring, trackPenaltyMinutes: v } })}
                label="Track penalty minutes"
              />
              <Switch
                checked={s.scoring.trackGoalTimestamps}
                onChange={(v) => persist({ scoring: { ...s.scoring, trackGoalTimestamps: v } })}
                label="Capture goal timestamps"
              />
            </div>
          )}
        </div>

        <div className="card">
          <div className="card__head"><h3 className="card__title">Tiebreakers</h3></div>
          <p className="card__sub">Top-down order; drag-equivalent buttons reorder.</p>
          <div className="stack">
            {s.scoring.tiebreakers.map((key, i) => (
              <div key={key} className="row row--between" style={{ padding: '6px 0' }}>
                <div className="row" style={{ gap: 8 }}>
                  <span className="kbd">{i + 1}</span>
                  <span>{TIEBREAKER_LABEL[key]}</span>
                </div>
                <div className="row" style={{ gap: 4 }}>
                  <button className="btn btn--ghost btn--sm" disabled={i === 0} onClick={() => moveTiebreaker(key, -1)}>Up</button>
                  <button className="btn btn--ghost btn--sm" disabled={i === s.scoring.tiebreakers.length - 1} onClick={() => moveTiebreaker(key, 1)}>Down</button>
                  <button className="btn btn--ghost btn--sm" onClick={() => toggleTiebreaker(key, false)}>Remove</button>
                </div>
              </div>
            ))}
            <div className="divider" />
            <div className="field">
              <label className="field__label">Add a tiebreaker</label>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {TIEBREAKER_OPTIONS.filter((k) => !s.scoring.tiebreakers.includes(k)).map((k) => (
                  <button key={k} className="btn btn--ghost btn--sm" onClick={() => toggleTiebreaker(k, true)}>
                    + {TIEBREAKER_LABEL[k]}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__head"><h3 className="card__title">Scheduling guardrails</h3></div>
          <div className="stack">
            <div className="field">
              <label className="field__label">Min team rest (minutes)</label>
              <input
                type="number"
                min={0}
                className="input"
                value={s.scheduling.minTeamRestMinutes}
                onChange={(e) =>
                  persist({ scheduling: { ...s.scheduling, minTeamRestMinutes: Number(e.target.value || 0) } })
                }
              />
            </div>
            <div className="field">
              <label className="field__label">Rink turnover (minutes)</label>
              <input
                type="number"
                min={0}
                className="input"
                value={s.scheduling.rinkTurnoverMinutes}
                onChange={(e) =>
                  persist({ scheduling: { ...s.scheduling, rinkTurnoverMinutes: Number(e.target.value || 0) } })
                }
              />
            </div>
            <Switch
              checked={s.scheduling.balanceBackToBacks}
              onChange={(v) => persist({ scheduling: { ...s.scheduling, balanceBackToBacks: v } })}
              label="Balance back-to-back games"
              hint="Smart Scheduler will avoid stacking the same team consecutively."
            />
          </div>
        </div>

        {isSeason && (
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Season cadence</h3>
                <p className="card__sub">Default regular-season rhythm used by Smart Scheduler.</p>
              </div>
            </div>
            {hasSchedule && (
              <div className="note note--warn" style={{ marginBottom: 12 }}>
                This season already has a saved schedule. Cadence changes apply to future schedule generation and may require regenerating games.
              </div>
            )}
            <div className="stack">
              <div className="field">
                <label className="field__label">Game nights</label>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  {WEEKDAYS.map((day) => {
                    const checked = season.weekdays.includes(day.value);
                    return (
                      <button
                        key={day.value}
                        type="button"
                        className={`btn btn--sm ${checked ? 'btn--primary' : 'btn--ghost'}`}
                        onClick={() => {
                          const weekdays = checked
                            ? season.weekdays.filter((value) => value !== day.value)
                            : Array.from(new Set([...season.weekdays, day.value]));
                          persist({ season: { ...season, weekdays } });
                        }}
                      >
                        {day.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid--3">
                <div className="field">
                  <label className="field__label">First puck time</label>
                  <input
                    type="time"
                    className="input"
                    value={season.firstPuckTime}
                    onChange={(e) => persist({ season: { ...season, firstPuckTime: e.target.value } })}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Games per rink/night</label>
                  <input
                    type="number"
                    min={1}
                    max={12}
                    className="input"
                    value={season.gamesPerRinkPerNight}
                    onChange={(e) => persist({ season: { ...season, gamesPerRinkPerNight: Math.max(1, Number(e.target.value || 1)) } })}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Regular season ends</label>
                  <input
                    type="date"
                    className="input"
                    value={dateOnly(season.regularSeasonEndsAt ?? t.endsAt)}
                    onChange={(e) => persist({ season: { ...season, regularSeasonEndsAt: fromDate(e.target.value) } })}
                  />
                </div>
              </div>
              <div className="field">
                <label className="field__label">Blackout dates</label>
                <input
                  className="input"
                  value={season.blackoutDates.map(dateOnly).join(', ')}
                  onChange={(e) => persist({ season: { ...season, blackoutDates: parseDateList(e.target.value) } })}
                  placeholder="2026-07-04, 2026-08-01"
                />
              </div>
            </div>
          </div>
        )}

        {isSeason && (
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Registration and roster intake</h3>
                <p className="card__sub">Controls how people enter the season before final roster review.</p>
              </div>
            </div>
            <div className="stack">
              <div className="field">
                <label className="field__label">Roster intake path</label>
                <select
                  className="select"
                  value={season.rosterIntake}
                  onChange={(e) => {
                    const rosterIntake = e.target.value as typeof season.rosterIntake;
                    persist({
                      season: {
                        ...season,
                        rosterIntake,
                        registration: { ...season.registration, enabled: rosterIntake === 'registration' },
                      },
                    });
                  }}
                >
                  <option value="upload">Import teams</option>
                  <option value="manual">Build manually</option>
                  <option value="registration">Open registration</option>
                  <option value="later">Decide later</option>
                </select>
              </div>
              <Switch
                checked={season.registration.enabled}
                onChange={(enabled) =>
                  persist({
                    season: {
                      ...season,
                      rosterIntake: enabled ? 'registration' : season.rosterIntake === 'registration' ? 'later' : season.rosterIntake,
                      registration: { ...season.registration, enabled },
                    },
                  })
                }
                label="Public registration enabled"
              />
              <div className="grid grid--3">
                <div className="field">
                  <label className="field__label">Intake type</label>
                  <select
                    className="select"
                    value={season.registration.intakeType}
                    onChange={(e) => persist({ season: { ...season, registration: { ...season.registration, intakeType: e.target.value as RegistrationIntakeType } } })}
                  >
                    <option value="individual">Individual players</option>
                    <option value="team">Team registration</option>
                    <option value="both">Teams and individuals</option>
                  </select>
                </div>
                <div className="field">
                  <label className="field__label">Opens</label>
                  <input
                    type="date"
                    className="input"
                    value={dateOnly(season.registration.opensAt)}
                    onChange={(e) => persist({ season: { ...season, registration: { ...season.registration, opensAt: fromDate(e.target.value) || undefined } } })}
                  />
                </div>
                <div className="field">
                  <label className="field__label">Closes</label>
                  <input
                    type="date"
                    className="input"
                    value={dateOnly(season.registration.closesAt)}
                    onChange={(e) => persist({ season: { ...season, registration: { ...season.registration, closesAt: fromDate(e.target.value) || undefined } } })}
                  />
                </div>
              </div>
              <div className="grid grid--3">
                <div className="field">
                  <label className="field__label">Fee</label>
                  <input
                    type="number"
                    min={0}
                    className="input"
                    value={season.registration.feeCents / 100}
                    onChange={(e) =>
                      persist({
                        season: {
                          ...season,
                          registration: {
                            ...season.registration,
                            feeCents: Math.max(0, Math.round(Number(e.target.value || 0) * 100)),
                          },
                        },
                      })
                    }
                  />
                </div>
                <Switch
                  checked={season.registration.requireWaiver}
                  onChange={(requireWaiver) => persist({ season: { ...season, registration: { ...season.registration, requireWaiver } } })}
                  label="Require waiver"
                />
                <Switch
                  checked={season.registration.autoApprove}
                  onChange={(autoApprove) => persist({ season: { ...season, registration: { ...season.registration, autoApprove } } })}
                  label="Auto-approve"
                />
              </div>
            </div>
          </div>
        )}

        {isSeason && (
          <div className="card">
            <div className="card__head">
              <div>
                <h3 className="card__title">Playoffs</h3>
                <p className="card__sub">Optional postseason seeded from standings or manually later.</p>
              </div>
            </div>
            {hasScoring && (
              <div className="note note--warn" style={{ marginBottom: 12 }}>
                Scored games exist. Changing playoff qualification will not rewrite historical standings.
              </div>
            )}
            <div className="stack">
              <Switch
                checked={Boolean(s.playoffs)}
                onChange={(enabled) => {
                  const next = enabled
                    ? { ...s, playoffs: s.playoffs ?? defaultPlayoffSettings() }
                    : { ...s, playoffs: undefined };
                  updateTournament(t.id, { settings: next });
                  setSavedAt(new Date().toLocaleTimeString());
                }}
                label="Include playoffs"
              />
              {s.playoffs && (
                <div className="grid grid--3">
                  <div className="field">
                    <label className="field__label">Format</label>
                    <select
                      className="select"
                      value={s.playoffs.format}
                      onChange={(e) => persist({ playoffs: { ...s.playoffs!, format: e.target.value as PlayoffFormat } })}
                    >
                      <option value="single_elim">Single elimination</option>
                      <option value="double_elim">Double elimination</option>
                      <option value="top_n_round_robin">Top-N round robin</option>
                    </select>
                  </div>
                  <div className="field">
                    <label className="field__label">Qualifying teams</label>
                    <input
                      type="number"
                      min={2}
                      max={64}
                      className="input"
                      value={s.playoffs.qualifyingTeams}
                      onChange={(e) => persist({ playoffs: { ...s.playoffs!, qualifyingTeams: Math.max(2, Number(e.target.value || 2)) } })}
                    />
                  </div>
                  <div className="field">
                    <label className="field__label">Seeding</label>
                    <select
                      className="select"
                      value={s.playoffs.seedingSource}
                      onChange={(e) => persist({ playoffs: { ...s.playoffs!, seedingSource: e.target.value as 'final_standings' | 'manual' } })}
                    >
                      <option value="final_standings">Final standings</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

      </div>

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <span className="muted" style={{ fontSize: 12 }}>
          <Save size={12} style={{ verticalAlign: -1, marginRight: 4 }} />
          Settings save automatically as you change them.
        </span>
      </div>
    </>
  );
}

function fromDate(date: string): string {
  if (!date) return '';
  return new Date(`${date}T00:00:00`).toISOString();
}

function dateOnly(iso?: string): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

function parseDateList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map(fromDate)
    .filter(Boolean);
}

function clampInt(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value || String(min), 10);
  if (Number.isNaN(parsed)) return min;
  return Math.max(min, Math.min(max, parsed));
}
